import React, { useLayoutEffect, useMemo, useRef, useState } from "react";

import { Box } from "@mui/material";
import type { CanvasCard, DataTableCardData, ReportOrientation, TableCardConfig } from "store/types";

import { DataTableCard } from "../components/cards/DataTableCard";
import { CardSizeProvider } from "../context/CardSizeContext";

/** DOM-measured heights for one table card. */
export interface MeasuredTable {
  /** Height of the table header row (thead). */
  header: number;
  /** Pixel height of each body row, keyed by its canonical `__path`. */
  rows: Map<string, number>;
}

export interface UseMeasuredTableHeights {
  /** null until the first measurement pass completes. */
  measured: Map<string, MeasuredTable> | null;
  /** Hidden element the consumer must render so the tables can be measured. */
  measureNode: React.ReactNode;
}

/**
 * Renders every table card's full table off-screen and measures the real pixel
 * height of each row (and the header) via the DOM. These exact heights drive
 * pixel-perfect pagination instead of font-width heuristics.
 *
 * The cards passed in must already carry `__path` on every row (see
 * `assignRowPaths`). `getWidth(pageWidth, gridW)` must return the same pixel
 * width the table renders at on the page (preview and canvas differ), so text
 * wraps identically and the measured heights match.
 */
export function useMeasuredTableHeights(
  tableCards: CanvasCard[],
  pageWidth: number,
  getWidth: (pageWidth: number, gridW: number) => number,
  orientation: ReportOrientation
): UseMeasuredTableHeights {
  const ref = useRef<HTMLDivElement>(null);
  const [measured, setMeasured] = useState<Map<string, MeasuredTable> | null>(null);

  // Re-measure when the set of tables, their data, width, or orientation changes.
  const signature = useMemo(
    () =>
      `${pageWidth}|${orientation}|` +
      tableCards
        .map((c) => `${c.id}:${c.gridW}:${(c.data as DataTableCardData | undefined)?.data?.length ?? 0}`)
        .join(","),
    [tableCards, pageWidth, orientation]
  );

  useLayoutEffect(() => {
    if (!ref.current) return;
    const map = new Map<string, MeasuredTable>();
    ref.current.querySelectorAll<HTMLElement>("[data-measure-card]").forEach((cardEl) => {
      const id = cardEl.getAttribute("data-measure-card");
      if (id == null) return;
      const headerEl = cardEl.querySelector<HTMLElement>("[data-measure-header]");
      const rows = new Map<string, number>();
      cardEl.querySelectorAll<HTMLElement>("[data-row-path]").forEach((tr) => {
        const path = tr.getAttribute("data-row-path");
        if (path != null) rows.set(path, tr.getBoundingClientRect().height);
      });
      map.set(id, { header: headerEl?.getBoundingClientRect().height ?? 0, rows });
    });
    setMeasured(map);
  }, [signature]);

  const measureNode = useMemo(
    () => (
      <Box
        ref={ref}
        aria-hidden
        sx={{
          position: "absolute",
          top: 0,
          left: -100000,
          visibility: "hidden",
          pointerEvents: "none",
          height: 0,
          overflow: "hidden"
        }}
      >
        {tableCards.map((card) => (
          <Box key={card.id} data-measure-card={card.id} sx={{ width: getWidth(pageWidth, card.gridW) }}>
            <CardSizeProvider value={{ widthPercent: card.widthPercent, orientation, isEditMode: false }}>
              <DataTableCard
                data={card.data as DataTableCardData | undefined}
                columnSettings={(card.config as TableCardConfig).columns}
                columnWidths={(card.config as TableCardConfig).columnWidths}
              />
            </CardSizeProvider>
          </Box>
        ))}
      </Box>
    ),
    [tableCards, pageWidth, getWidth, orientation]
  );

  return { measured, measureNode };
}
